import React from 'react';
import { Alert, Button, Spinner, Badge } from 'react-bootstrap';
import { AlertTriangle, Save, ExternalLink } from 'lucide-react';
import type { CpGpMatchedProduct } from '../../../../../../models/Coupang';
import type { RestructuredProduct } from '../../../../../../infrastructure/SourcingClient';
import { IdBadge, IdBadgeType } from '../../../../../../components/ui/badge/IdBadge';
import { ThumbnailSelector } from './ThumbnailSelector';
import { GalleryImageEditor } from './GalleryImageEditor';
import { getProductTypeColor } from '../../../../../../utils/statusUtils';
import type { SkinSeoulProductType } from '../../../../../../models/ProductTypes';

interface ImageSelection {
  thumbnails: Record<string, string>;
  gallery: string[];
  pdpImages: string[];
}

interface ProductThatNeedsThumbnail {
  product: RestructuredProduct;
  productKey: string;
  displayName: string;
  productType: SkinSeoulProductType;
  attributePack: string;
  attributeSize: string;
  attributeColor: string;
  recommendedImage: string | undefined;
  is1Pack: boolean;
}

interface THUImageEditorProps {
  readonly matchedProduct: CpGpMatchedProduct;
  readonly productsThatNeedThumbnails: ProductThatNeedsThumbnail[];
  readonly getSKUFromCpItemId: (cpItemId: string) => string | undefined;
  readonly imageSelection: ImageSelection;
  readonly sourceImages: {
    hero: string[];
    detailed: string[];
    gallery: string[];
    opt: string[];
    optionImages: string[];
    heroImagesForSelection: string[];
  };
  readonly fileInputRef: Record<string, HTMLInputElement | null>;
  readonly saveLoading: boolean;
  readonly onThumbnailSelect: (productKey: string, imageUrl: string) => void;
  readonly onDragStart: (e: React.DragEvent, imageUrl: string, productKey: string) => void;
  readonly onDrop: (e: React.DragEvent, productKey: string) => void;
  readonly onDragOver: (e: React.DragEvent) => void;
  readonly onFileUpload: (event: React.ChangeEvent<HTMLInputElement>, productKey?: string) => void;
  readonly onSaveFeaturedThumbnails: () => Promise<void>;
  readonly productsBySku?: any[];
  readonly onRefetchProducts?: () => void;
  readonly onGalleryToggle: (imageUrl: string) => void;
  readonly onSaveGalleryImages: () => Promise<void>;
}

export const THUImageEditor: React.FC<THUImageEditorProps> = ({
  matchedProduct,
  productsThatNeedThumbnails,
  imageSelection,
  sourceImages,
  fileInputRef,
  saveLoading,
  getSKUFromCpItemId,
  onThumbnailSelect,
  onDragStart,
  onDrop,
  onDragOver,
  onFileUpload,
  onSaveFeaturedThumbnails,
  onGalleryToggle,
  onSaveGalleryImages,
}) => {
  return (
    <div>
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <h4 className="mb-0">Featured Thumbnails</h4>
        <Button variant="success" size="sm" onClick={onSaveFeaturedThumbnails} disabled={saveLoading}>
          {saveLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            <>
              <Save size={14} className="me-2" />
              Save Featured Thumbnails
            </>
          )}
        </Button>
      </div>

      {productsThatNeedThumbnails.length === 0 ? (
        <Alert variant="warning">
          <AlertTriangle size={18} className="me-2" />
          No Variable or Simple products found. Please restructure the product first to generate the product structure.
        </Alert>
      ) : (
        <>
          <div className="d-flex flex-column gap-3">
            {productsThatNeedThumbnails.map((item, index) => {
              const selectedImageUrl = imageSelection.thumbnails[item.productKey];
              const candidateImageUrl = item.recommendedImage;

              // Helper function to extract cpItemId from image URL
              const extractCpItemIdFromUrl = (url: string | undefined): string | null => {
                if (!url) {
                  return null;
                }
                const filename = url.split('/').pop() || '';
                // Match patterns like opt_xxx_itemId_x.webp, thu_xxx_itemId_x.webp, hov_xxx_itemId_x.webp, hover_xxx_itemId_x.webp
                const match = filename.match(/(?:opt|thu|hover|hov)_\d+_(\d+)_\d+\.webp/);
                return match && match[1] ? match[1] : null;
              };

              // Collect thumbnail images matching this SKU's cpItemId
              const currentCpItemId = item.product.cpItemId;
              const allThumbnails: string[] = [];
              const fromRestructured: string[] = [];
              const fromOptions: string[] = [];

              // 1. Collect from restructuredProducts (hoverImage and imageUrl) - filter by cpItemId
              const allRestructuredProducts = matchedProduct.ssRestructuredProducts || [];
              allRestructuredProducts.forEach((product: any) => {
                if (product.hoverImageUrl && !allThumbnails.includes(product.hoverImageUrl)) {
                  const urlCpItemId = extractCpItemIdFromUrl(product.hoverImageUrl);
                  if (urlCpItemId === currentCpItemId) {
                    allThumbnails.push(product.hoverImageUrl);
                    fromRestructured.push(product.hoverImageUrl);
                  }
                }
                if (product.imageUrl && !allThumbnails.includes(product.imageUrl)) {
                  const urlCpItemId = extractCpItemIdFromUrl(product.imageUrl);
                  if (urlCpItemId === currentCpItemId) {
                    allThumbnails.push(product.imageUrl);
                    fromRestructured.push(product.imageUrl);
                  }
                }
              });

              // 2. Collect from cpDetailedProduct.options (imageUrl and hoverImageUrl) - filter by cpItemId
              const cpOptions = (matchedProduct.cpDetailedProduct as any)?.options;
              if (Array.isArray(cpOptions)) {
                cpOptions.forEach((option: any) => {
                  if (option.hoverImageUrl && !allThumbnails.includes(option.hoverImageUrl)) {
                    const urlCpItemId = extractCpItemIdFromUrl(option.hoverImageUrl);
                    if (urlCpItemId === currentCpItemId) {
                      allThumbnails.push(option.hoverImageUrl);
                      fromOptions.push(option.hoverImageUrl);
                    }
                  }
                  if (option.imageUrl && !allThumbnails.includes(option.imageUrl)) {
                    const urlCpItemId = extractCpItemIdFromUrl(option.imageUrl);
                    if (urlCpItemId === currentCpItemId) {
                      allThumbnails.push(option.imageUrl);
                      fromOptions.push(option.imageUrl);
                    }
                  }
                });
              }

              console.info(`🖼️ [SKU ${index + 1}] Thumbnails for cpItemId ${currentCpItemId}:`, {
                cpItemId: currentCpItemId,
                displayName: item.displayName,
                selectedImageUrl: selectedImageUrl || 'Not selected yet',
                candidateImageUrl: candidateImageUrl || 'No candidate',
                fromRestructuredCount: fromRestructured.length,
                fromRestructured,
                fromOptionsCount: fromOptions.length,
                fromOptions,
                allThumbnailsCount: allThumbnails.length,
                allThumbnails,
              });

              return (
                <div
                  key={item.productKey}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '2px solid #dee2e6',
                  }}
                >
                  <div>
                    {/* Product Details */}
                    <div>
                      <div className="mb-3">
                        {/* Product Name with ID Badges and Go to CP button */}
                        <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <h6 className="mb-0">{item.displayName}</h6>
                            <Badge
                              bg={getProductTypeColor(item.productType)}
                              style={{ fontSize: '0.7rem', textTransform: 'capitalize' }}
                            >
                              {item.productType as SkinSeoulProductType}
                            </Badge>
                            {matchedProduct.cpDetailedProduct.productId && (
                              <IdBadge type={IdBadgeType.CPPI} value={matchedProduct.cpDetailedProduct.productId} />
                            )}
                            {item.product.cpItemId && <IdBadge type={IdBadgeType.CPII} value={item.product.cpItemId} />}
                            {item.product.cpItemId && getSKUFromCpItemId(item.product.cpItemId) && (
                              <IdBadge type={IdBadgeType.SKU} value={getSKUFromCpItemId(item.product.cpItemId)!} />
                            )}
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              window.open(
                                `https://www.coupang.com/vp/products/${matchedProduct.cpDetailedProduct.productId}`,
                                '_blank',
                              )
                            }
                            style={{ fontSize: '0.75rem', padding: '4px 8px' }}
                          >
                            <ExternalLink size={12} className="me-1" />
                            Go to CP
                          </Button>
                        </div>

                        {/* Product Attributes - Text with Strong */}
                        <div style={{ fontSize: '0.85rem', color: '#495057' }}>
                          {item.attributeSize && (
                            <span className="me-3">
                              Size: <strong style={{ color: '#0d6efd' }}>{item.attributeSize}</strong>
                            </span>
                          )}
                          {item.attributeColor && (
                            <span className="me-3">
                              Color: <strong style={{ color: '#0d6efd' }}>{item.attributeColor}</strong>
                            </span>
                          )}
                          {item.attributePack && (
                            <span>
                              Pack: <strong style={{ color: '#0d6efd' }}>{item.attributePack}</strong>
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Featured Thumbnails */}
                      <ThumbnailSelector
                        productKey={item.productKey}
                        selectedImageUrl={selectedImageUrl}
                        candidateImageUrl={candidateImageUrl}
                        optionImages={allThumbnails}
                        fileInputRef={fileInputRef}
                        onThumbnailSelect={onThumbnailSelect}
                        onDragStart={onDragStart}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        onFileUpload={onFileUpload}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <GalleryImageEditor
            heroImages={sourceImages.heroImagesForSelection}
            selectedGalleryImages={imageSelection.gallery}
            fileInputRef={fileInputRef}
            saveLoading={saveLoading}
            onGalleryToggle={onGalleryToggle}
            onFileUpload={onFileUpload}
            onSaveGalleryImages={onSaveGalleryImages}
            onDragStart={(e, imageUrl) => onDragStart(e, imageUrl, 'gallery-shared')}
          />
        </>
      )}
    </div>
  );
};
